import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Legend,
  ReferenceLine, ResponsiveContainer,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/* FBM curve palette. Order MUST match the legend order. */
const SERIES = [
  { key: "finishing", label: "Finishing",    stroke: "#3B82F6", fill: "#3B82F6" }, // blue
  { key: "defending", label: "Defending",    stroke: "#EF4444", fill: "#EF4444" }, // red
  { key: "passing",   label: "Passing Game", stroke: "#EAB308", fill: "#EAB308" }, // yellow/amber
  { key: "overall",   label: "Overall",      stroke: "#10B981", fill: "#10B981" }, // emerald
];

function CustomLegend({ payload }) {
  // Force our ordering, ignore recharts' order
  return (
    <ul
      data-testid="distribution-legend"
      className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-2 pb-1 pt-2"
    >
      {SERIES.map((s) => (
        <li key={s.key} className="flex items-center gap-2 text-xs font-display uppercase tracking-[0.14em]">
          <span className="inline-block h-2.5 w-3.5 rounded-[2px]" style={{ background: s.stroke }} />
          <span className="text-foreground/80">{s.label}</span>
        </li>
      ))}
    </ul>
  );
}

export default function DistributionChart({
  playerId, teamId, basePath, range: rangeProp,
  onAvailableRangesChange, playerName, teamName,
  // Phase 12 — when valuesOverride is provided (4 metric numbers), we render
  // those simulated curves instead of the player's live distribution. The
  // available_ranges from the live endpoint are still bubbled up so the
  // parent's range selector stays populated.
  valuesOverride, overrideLabel,
}) {
  // Accepts EITHER playerId or teamId. teamId takes precedence.
  // Phase 11: when used in player mode, the `range` is now CONTROLLED by the
  // parent (PlayerDetailPage owns the selector). For teams or older callers
  // that don't pass a `range` prop, we keep an internal fallback state.
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [internalRange, setInternalRange] = useState("career");
  const range = rangeProp ?? internalRange;
  const controlled = rangeProp !== undefined;
  const [refreshing, setRefreshing] = useState(false);

  const isPlayer = !teamId;
  const endpoint = teamId
    ? `/teams/${teamId}/distribution`
    : `/players/${playerId}/distribution`;

  const overrideActive = !!valuesOverride;
  const overrideKey = valuesOverride
    ? JSON.stringify(valuesOverride)
    : "";

  useEffect(() => {
    let alive = true;
    if (data) setRefreshing(true); else { setData(null); setError(null); }
    const params = isPlayer ? { range } : undefined;
    api.get(endpoint, { params })
      .then((r) => {
        if (!alive) return;
        // Bubble available_ranges up so the page-level selector can populate.
        if (isPlayer && onAvailableRangesChange && r.data?.available_ranges) {
          onAvailableRangesChange(r.data.available_ranges);
        }
        // When the override is active we DROP the live curves but keep the
        // available_ranges metadata. The override-effect below will paint
        // the simulated curves shortly after.
        if (!overrideActive) {
          setData(r.data); setError(null);
        }
      })
      .catch((e) => { if (alive && !overrideActive) setError(e?.response?.data?.detail || "Failed to load distribution"); })
      .finally(() => { if (alive) setRefreshing(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, ignoreFriendlies, range, overrideActive]);

  // Phase 12 — paint simulated curves whenever valuesOverride changes.
  useEffect(() => {
    if (!overrideActive) return;
    let alive = true;
    setRefreshing(true);
    api.post("/distribution-from-values", valuesOverride)
      .then((r) => {
        if (!alive) return;
        // Preserve `available_ranges` from the previously fetched live payload
        setData((prev) => ({ ...r.data, available_ranges: prev?.available_ranges || [] }));
        setError(null);
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to simulate distribution"); })
      .finally(() => { if (alive) setRefreshing(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideActive, overrideKey]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.x.map((x, i) => ({
      x,
      finishing: data.finishing[i],
      defending: data.defending[i],
      passing: data.passing[i],
      overall: data.overall[i],
    }));
  }, [data]);

  if (error) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="distribution-error">
        Could not load distribution chart: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="distribution-loading">
        Loading distribution chart…
      </div>
    );
  }

  const ranges = data.available_ranges || [];

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="distribution-chart">
      <header className="px-6 pt-6 pb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Behaviour profile</div>
          <h2 className="font-display uppercase tracking-tight text-xl" data-testid="distribution-card-title">
            {playerName ? `${playerName}'s Probability Distributions`
              : teamName ? `${teamName}'s Probability Distributions`
              : "Probability distributions"}
            {overrideActive && (
              <span className="ml-2 text-xs uppercase tracking-[0.2em] text-amber-500/90" data-testid="distribution-sim-tag">
                · {overrideLabel || "Transfer simulation"}
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-end gap-3">
          <div className="hidden md:block text-xs text-muted-foreground max-w-[16rem]">
            Dotted markers show where this player sits on each curve.
            {refreshing && <span className="ml-2 uppercase tracking-[0.18em] text-[10px]">· loading…</span>}
          </div>
          {/* Phase 11: the range selector lives on the parent page now (player mode).
              We keep an inline selector only for legacy/uncontrolled callers. */}
          {isPlayer && !controlled && ranges.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Range{refreshing ? " · loading…" : ""}
              </span>
              <Select value={range} onValueChange={setInternalRange}>
                <SelectTrigger
                  className="h-9 min-w-[180px]"
                  data-testid="distribution-range-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ranges.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      data-testid={`distribution-range-option-${r.value.replace("/", "-")}`}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </header>

      <div className="px-2 pt-2 pb-4">
        <div style={{ width: "100%", height: 400, minHeight: 400 }} data-testid="distribution-chart-canvas">
          <ResponsiveContainer width="100%" height="100%" minWidth={320} minHeight={320}>
            <AreaChart data={chartData} margin={{ top: 18, right: 28, left: 4, bottom: 22 }}>
              <defs>
                {SERIES.map((s) => (
                  <linearGradient key={s.key} id={`fbm-grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={s.fill} stopOpacity={0.5} />
                    <stop offset="55%"  stopColor={s.fill} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={s.fill} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>

              <CartesianGrid stroke="currentColor" strokeOpacity={0.06} vertical={false} />

              <XAxis
                dataKey="x"
                type="number"
                domain={[0, 100]}
                ticks={[0, 20, 40, 60, 80, 100]}
                tick={{ fontSize: 11, fill: "currentColor", opacity: 0.55 }}
                tickLine={false}
                axisLine={{ stroke: "currentColor", strokeOpacity: 0.15 }}
                label={{
                  value: "Probability (%)",
                  position: "insideBottom",
                  offset: -10,
                  style: { fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", fill: "currentColor", opacity: 0.55 },
                }}
              />
              <YAxis hide />

              <Legend content={<CustomLegend />} verticalAlign="top" wrapperStyle={{ paddingBottom: 6 }} />

              {/* Curves, in the legend order */}
              {SERIES.map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.stroke}
                  strokeWidth={2.5}
                  fill={`url(#fbm-grad-${s.key})`}
                  isAnimationActive
                  animationDuration={650}
                  activeDot={false}
                  dot={false}
                />
              ))}

              {/* "You are here" markers — vertical dashed line at each metric's actual probability */}
              {SERIES.map((s, idx) => {
                const v = data.values?.[s.key];
                if (v == null) return null;
                const xv = Math.round(v);
                // Stagger labels vertically so overlapping values stay readable
                const labelOffset = 6 + (idx * 14);
                return (
                  <ReferenceLine
                    key={`ref-${s.key}`}
                    x={xv}
                    stroke={s.stroke}
                    strokeDasharray="2 4"
                    strokeOpacity={0.85}
                    ifOverflow="extendDomain"
                    label={{
                      value: `${s.label.split(" ")[0]} ${v.toFixed(1)}`,
                      position: "insideTopLeft",
                      fill: s.stroke,
                      fontSize: 10,
                      offset: labelOffset,
                      dy: labelOffset,
                      dx: 4,
                      fontFamily: "var(--font-display, inherit)",
                    }}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
